import { useAuth } from '@/context/AuthContext';
import { Building2 } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

/**
 * Role-aware school selector (single source of truth for school scoping UI).
 *
 * - school_admin / staff (one school): no dropdown, static school name.
 * - kommun_admin / superadmin (tenant-wide): dropdown with "Alla skolor".
 * - user with a subset of several schools: dropdown with "Alla mina skolor".
 *
 * Backend remains the source of truth for access; this only controls what the
 * user is offered. `schools` is the already server-scoped list from GET /schools.
 */
export function SchoolSelector({ schools = [], value, onChange, className, triggerClassName }) {
  const { isSuperAdmin, isKommunAdmin } = useAuth();
  const tenantWide = isSuperAdmin() || isKommunAdmin();

  // Single-school user: never show a selector that implies multi-school access.
  if (!tenantWide && schools.length <= 1) {
    const name = schools[0]?.name;
    if (!name) return null;
    return (
      <div
        className={cn("inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-200 bg-slate-50 text-sm font-ibm text-slate-700", className)}
        data-testid="school-static-label"
      >
        <Building2 className="h-3.5 w-3.5 text-slate-400" />
        {name}
      </div>
    );
  }

  const allLabel = tenantWide ? 'Alla skolor' : 'Alla mina skolor';

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={cn("w-56", triggerClassName)} data-testid="school-selector">
        <Building2 className="h-3.5 w-3.5 mr-1.5" />
        <SelectValue placeholder={allLabel} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{allLabel}</SelectItem>
        {schools.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
